import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { useResponsive, useRoleNavigation, useAutoTranslate, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { processesApi, goalsApi, organizationalUnitsApi, kpisApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnitList, KPIType, Polarity, Frequency, Process } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { IconButton } from '@design/components/IconButton';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
} from '../kpis/components/KpiAnnualTargetSection';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddProcess'>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);


// ============================================================
// Portal-based Dropdown Component with Fixed Positioning
// ============================================================
interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<View>;
  children: React.ReactNode;
  backgroundColor: string;
  borderColor: string;
}

const PortalDropdown = ({ isOpen, onClose, buttonRef, children, backgroundColor, borderColor }: DropdownProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false, availableHeight: 300 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, width: number, height: number) => {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - (y + height + 4);
        const spaceAbove = y - 4;
        const maxDropdown = 300;
        const openUpward = spaceBelow < 150 && spaceAbove > spaceBelow;
        setPosition({
          top: openUpward ? Math.max(4, y - Math.min(maxDropdown, spaceAbove)) : y + height + 4,
          left: x,
          width: width,
          openUpward,
          availableHeight: openUpward ? Math.min(maxDropdown, spaceAbove) : Math.min(maxDropdown, spaceBelow),
        });
      });
    }
  }, [isOpen, buttonRef]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown itself
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dropdownMenu = (
    <>
      {/* eslint-disable-next-line */}
      <div
        onMouseDown={(e: any) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        style={{
          position: 'fixed' as const,
          inset: 0,
          zIndex: 9998,
        }}
      />
      <div
        ref={dropdownRef}
        onMouseDown={(e: any) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: position.top,
          insetInlineStart: position.left,
          width: position.width,
          backgroundColor: backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: tokens.radii.absolute["radius-8"],
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: position.availableHeight,
          overflowY: 'auto' as const,
        }}
      >
        {children}
      </div>
    </>
  );

  return ReactDOM.createPortal(dropdownMenu, document.body);
};

// KPI Form Data Interface
interface KPIFormData {
  id: string;
  name: string;
  description: string;
  weight: string;
  kpiType: string;
  polarity: string;
  unit: string;
  targetValue: string;
  worstAcceptableValue: string;
  frequency: string;
  formula: string;
  dataSource: string;
  measurementStartDate: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  responsibleUnits: string[];
}

const createEmptyKPI = (): KPIFormData => ({
  id: Math.random().toString(36).substr(2, 9),
  name: '',
  description: '',
  weight: '',
  kpiType: '',
  polarity: '',
  unit: '%',
  // New KPIs author the target through the annual grid; the scalar stays at the
  // '0.0' sentinel so the required-field check passes (matches every other inline
  // KPI screen). '' here made the grid-authored target fail validation → the
  // "fill all required fields" error on save. See utils/kpiErrors.ts.
  targetValue: '0.0',
  worstAcceptableValue: '',
  frequency: 'yearly',
  formula: '',
  dataSource: '',
  measurementStartDate: '',
  target: emptyKpiTargetConfig,
  isExpanded: true,
  responsibleUnits: [],
});

export default function AddProcessScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const processId = route.params?.processId;
  const scrollToKpi = route.params?.scrollToKpi;
  const goalId = route.params?.goalId;
  const organizationalUnitId = route.params?.organizationalUnitId;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateUnit } = useAutoTranslate();

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : tokens.colors.raw.gray["100"],
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiHeader: colors.card,
    kpiHeaderTitle: colors.textPrimary,
    kpiNumberBadge: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["200"],
    kpiSubsection: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addKpiButton: colors.card,
    addKpiButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addKpiButtonText: colors.textPrimary,
    loadingText: colors.textMuted,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };
  const isEditMode = !!processId;

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly) {
      console.log('🚫 AddProcess: View-only user - redirecting');
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    }
  }, [isViewOnly, navigation]);

  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionRef = useRef<View>(null);
  const goalDropdownButtonRef = useRef<View>(null);
  const [kpiSectionY, setKpiSectionY] = useState(0);

  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoals: [] as number[],
    organizationalUnits: [] as number[],
    weight: '0',
  });

  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // Dropdowns
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);

  // KPI Dropdown state: { kpiId, dropdownType }
  const [activeKpiDropdown, setActiveKpiDropdown] = useState<{ kpiId: string; type: string } | null>(null);

  const isSmallScreen = width < 768;

  // Dropdown options for KPIs
  const kpiTypeOptions = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];


  // Toggle KPI dropdown
  const toggleKpiDropdown = (kpiId: string, dropdownType: string) => {
    if (activeKpiDropdown?.kpiId === kpiId && activeKpiDropdown?.type === dropdownType) {
      setActiveKpiDropdown(null);
    } else {
      setActiveKpiDropdown({ kpiId, type: dropdownType });
    }
    // Close other dropdowns
    setShowGoalDropdown(false);
  };

  // Close all KPI dropdowns
  const closeKpiDropdowns = () => {
    setActiveKpiDropdown(null);
  };

  useEffect(() => {
    fetchData();
  }, [processId]);

  // Scroll to KPI section if requested
  useEffect(() => {
    if (scrollToKpi && !loading && kpiSectionY > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY - 100, animated: true });
      }, 300);
    }
  }, [scrollToKpi, loading, kpiSectionY]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [goalsData, unitsData] = await Promise.all([
        goalsApi.list({ page_size: 100 }),
        organizationalUnitsApi.list({ page_size: 100 }),
      ]);
      setGoals(goalsData.results || []);
      setUnits(unitsData.results || []);

      // Pre-select organizational unit if passed from unit detail page
      if (!processId && organizationalUnitId) {
        setFormData(prev => ({ ...prev, organizationalUnits: [organizationalUnitId] }));
      }

      // If editing, fetch the process data
      if (processId) {
        const processData = await processesApi.get(processId);
        setFormData({
          name: processData.name || '',
          description: processData.description || '',
          strategicGoals: processData.strategic_goals || [],
          organizationalUnits: processData.organizational_units || [],
          weight: processData.weight || '0',
        });

        // Fetch existing KPIs for this process
        const existingKpis = await kpisApi.list({ source_process: processId, page_size: 100 });
        if (existingKpis.results && existingKpis.results.length > 0) {
          const loadedKpis: KPIFormData[] = existingKpis.results.map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            description: kpi.description || '',
            weight: kpi.weight?.toString() || '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            targetValue: kpi.target_value?.toString() || '',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            dataSource: kpi.data_source || '',
            measurementStartDate: kpi.measurement_start_date || '',
            target: configFromKpi(kpi),
            isExpanded: false,
            responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
          }));
          setKpis(loadedKpis);
        }
      } else if (goalId) {
        // Pre-select the goal when creating from GoalDetailScreen
        setFormData(prev => ({
          ...prev,
          strategicGoals: [goalId],
        }));
      }
    } catch (err) {
      showError(t('processes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.processNameRequired');
    }
    if (formData.strategicGoals.length === 0) {
      newErrors.strategicGoals = t('validation.strategicGoalRequired');
    }
    if (formData.organizationalUnits.length === 0) {
      newErrors.organizationalUnits = t('validation.organizationalUnitRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t, 'measurementStartDate');
    if (kpiError) { showError(kpiError); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      // Get user's organization
      const organizationId = user?.profile?.organization;
      if (!organizationId) {
        showError(t('common.organizationNotFound'));
        return;
      }

      // Prepare process data
      const processData: Partial<Process> = {
        department_id: organizationId,
        name: formData.name,
        description: formData.description || '',
        strategic_goals: formData.strategicGoals,
        organizational_units: formData.organizationalUnits,
        weight: formData.weight || '0',
      };

      let savedProcessId: number;

      if (isEditMode && processId) {
        // Update existing process
        console.log('Updating process with data:', processData);
        await processesApi.update(processId, processData);
        savedProcessId = processId;
      } else {
        // Create new process
        console.log('Creating process with data:', processData);
        const createdProcess = await processesApi.create(processData);
        savedProcessId = createdProcess.id;
      }

      // Handle KPIs - create new ones and update existing ones
      const kpiErrors: string[] = [];
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      for (const kpi of validKpis) {
        const kpiData = {
          name: kpi.name,
          description: kpi.description,
          source_process: savedProcessId,
          kpi_type: (kpi.kpiType || 'operational') as KPIType,
          polarity: (kpi.polarity || 'positive') as Polarity,
          unit: kpi.unit || 'number',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          worst_acceptable_value:
            kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
              ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
              : null,
          frequency: (kpi.frequency || 'monthly') as Frequency,
          measurement_formula: kpi.formula,
          data_source: kpi.dataSource,
          measurement_start_date: kpi.measurementStartDate || null,
          weight: kpi.weight || '0',
          source_type: 'process',
          responsible_units: kpi.responsibleUnits.map(Number),
        };

        const numericId = parseInt(kpi.id, 10);
        if (!isNaN(numericId) && numericId > 0) {
          try {
            await kpisApi.update(numericId, {
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.measurementStartDate,
              }),
            });
          } catch (err: any) {
            if (err.response?.status === 404) {
              try {
                await kpisApi.create(kpiData);
              } catch (createError: any) {
                const errMsg = extractKpiErrorMessage(createError?.response?.data);
                kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              }
            } else {
              const errMsg = extractKpiErrorMessage(err?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            }
          }
        } else {
          try {
            await kpisApi.create({
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.measurementStartDate,
              }),
            });
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
          }
        }
      }

      if (kpiErrors.length > 0) {
        showError(kpiErrors.join('\n'));
      }

      showSuccess(isEditMode ? t('processes.updateSuccess') : t('processes.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      // Go to the saved process's detail page instead of goBack()/list.
      if (savedProcessId) {
        navigation.navigate('ProcessDetail', { id: savedProcessId });
      } else if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    } catch (err: any) {
      console.error('Error saving process:', err);
      console.error('Error response:', err.response?.data);

      // Extract error message from various formats
      let errorMessage = isEditMode ? t('processes.updateFailed') : t('processes.createFailed');
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = data.non_field_errors[0];
        } else {
          // Check for field-specific errors
          const fieldErrors = Object.entries(data)
            .filter(([key, value]) => Array.isArray(value))
            .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
            .join('\n');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Processes' as any);
    }
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis },
    ready: !loading,
    onExit: handleCancel,
  });

  const addKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  const removeKPI = (id: string) => {
    const target = kpis.find(k => k.id === id);
    const numericId = parseInt(id, 10);
    // String IDs from createEmptyKPI (Math.random()) → NaN → treat as new (unsaved)
    const backendId = !isNaN(numericId) && numericId > 0 ? numericId : null;
    setKpiPendingDelete({
      id,
      backendId,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKPI = async () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      try {
        await kpisApi.delete(backendId);
        showSuccess(t('kpis.deleteSuccess'));
      } catch (err) {
        showError(t('kpis.deleteFailed'));
        setKpiPendingDelete(null);
        return;
      }
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  const updateKPI = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, [field]: value } : kpi));
  };

  const toggleKPIExpanded = (id: string) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  const orgUnitOptions = units.map(u => ({ value: u.id.toString(), label: translateUnit(u.name_ar || u.name) }));

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.processes'), route: 'Processes' },
              { label: isEditMode ? t('processes.editProcess') : t('processes.addMainProcess') },
            ]}
          />
   {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('processes.editProcess') : t('processes.addMainProcess')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: showGoalDropdown ? 9999 : 20, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showGoalDropdown ? 9999 : 10, overflow: 'visible' }]}>
              {/* Process Name - RIGHT side in RTL */}
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('processes.processName')} <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>

              {/* Strategic Goals - LEFT side in RTL (multi-select) */}
              <View style={[styles.formGroup, styles.formGroupHalf, { overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('goals.strategicGoals')} <Text style={styles.required}>*</Text>
                </Text>
                <View style={styles.dropdownWrapper}>
                  <TouchableOpacity
                    ref={goalDropdownButtonRef as any}
                    style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder, minHeight: 44, height: 'auto' as any, flexWrap: 'wrap' as any }, errors.strategicGoals ? styles.inputError : undefined]}
                    onPress={() => {
                      setShowGoalDropdown(!showGoalDropdown);
                      setActiveKpiDropdown(null);
                    }}
                  >
                    <ChevronDownIcon size={16} color={themeColors.iconColor} />
                    {formData.strategicGoals.length > 0 ? (
                      <View style={{ flex: 1, flexDirection: 'row-reverse', flexWrap: 'wrap', gap: tokens.spacing.absolute["1_5"] }}>
                        {formData.strategicGoals.map(gid => {
                          const goal = goals.find(g => g.id === gid);
                          return (
                            <View key={gid} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: tokens.colors.raw.amber["100"], borderRadius: tokens.radii.absolute["radius-12"], paddingHorizontal: tokens.spacing.absolute["2"], paddingVertical: tokens.spacing.absolute["1"] }}>
                              <TouchableOpacity
                                onPress={(e) => {
                                  e.stopPropagation();
                                  setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== gid) });
                                }}
                                style={{ marginStart: tokens.spacing.absolute["1"] }}
                              >
                                <Text style={{ color: tokens.colors.semantic.general["Main Color"], fontSize: tokens.typography.paragraph["paragraph small font size"], fontWeight: '700' }}>×</Text>
                              </TouchableOpacity>
                              <Text style={{ color: tokens.colors.semantic.general["Main Color"], fontSize: tokens.typography.paragraph["paragraph mini font size"], fontFamily: 'JF-Flat-Regular' }} numberOfLines={1}>
                                {translate(goal?.title) || `#${gid}`}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    ) : (
                      <Text style={[styles.dropdownText, { color: themeColors.dropdownText }]}>
                        {t('goals.selectStrategicGoal')}
                      </Text>
                    )}
                  </TouchableOpacity>
                  <PortalDropdown
                    isOpen={showGoalDropdown}
                    onClose={() => setShowGoalDropdown(false)}
                    buttonRef={goalDropdownButtonRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                      {goals.length === 0 ? (
                        <View style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}>
                          <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{t('common.noData')}</Text>
                        </View>
                      ) : (
                        goals.map(goal => {
                          const isSelected = formData.strategicGoals.includes(goal.id);
                          return (
                            <TouchableOpacity
                              key={goal.id}
                              style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }, isSelected && { backgroundColor: tokens.colors.raw.amber["100"] }]}
                              onPress={() => {
                                if (isSelected) {
                                  setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== goal.id) });
                                } else {
                                  setFormData({ ...formData, strategicGoals: [...formData.strategicGoals, goal.id] });
                                }
                                setErrors({ ...errors, strategicGoals: '' });
                              }}
                            >
                              <View style={{ width: 20, height: 20, borderRadius: tokens.radii.absolute["radius-4"], borderWidth: 1.5, borderColor: isSelected ? tokens.colors.semantic.general["Main Color"] : tokens.colors.raw.gray["300"], backgroundColor: isSelected ? tokens.colors.semantic.general["Main Color"] : 'transparent', alignItems: 'center', justifyContent: 'center', marginStart: tokens.spacing.absolute["2"] }}>
                                {isSelected && <Text style={{ color: '#FFF', fontSize: tokens.typography.paragraph["paragraph mini font size"], fontWeight: '700' }}>✓</Text>}
                              </View>
                              <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }, isSelected && { fontWeight: '600' }]}>{translate(goal.title)}</Text>
                            </TouchableOpacity>
                          );
                        })
                      )}
                    </ScrollView>
                  </PortalDropdown>
                </View>
                {errors.strategicGoals && <Text style={styles.errorText}>{errors.strategicGoals}</Text>}
              </View>
            </View>

            {/* Description */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('processes.processDescription')} <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                multiline
                numberOfLines={3}
                textAlign="right"
                textAlignVertical="top"
              />
            </View>

            {/* Organizational Units - Multi-select */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('initiatives.implementationResponsibility')} <Text style={styles.required}>*</Text>
              </Text>
              <MultiSelectDropdown
                values={formData.organizationalUnits.map(String)}
                options={orgUnitOptions}
                placeholder={t('initiatives.selectResponsibleUnits')}
                onChange={(vals) => {
                  setFormData({ ...formData, organizationalUnits: vals.map(Number) });
                  if (errors.organizationalUnits) setErrors({ ...errors, organizationalUnits: '' });
                }}
              />
              {errors.organizationalUnits && <Text style={styles.errorText}>{errors.organizationalUnits}</Text>}
            </View>
          </View>

          {/* KPIs Section */}
          <View
            style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: activeKpiDropdown ? 9999 : 10, overflow: 'visible' }]}
            onLayout={(event) => {
              const { y } = event.nativeEvent.layout;
              setKpiSectionY(y);
            }}
          >
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('kpis.performanceIndicators')}</Text>

            {kpis.map((kpi, index) => (
              <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }, activeKpiDropdown?.kpiId === kpi.id && { zIndex: 100 }]}>
                {/* KPI Header */}
                <View style={[styles.kpiHeader, { backgroundColor: themeColors.kpiHeader }]}>
                  {/* LEFT side (in RTL): Expand/Collapse, Trash */}
                  <TouchableOpacity onPress={() => toggleKPIExpanded(kpi.id)}>
                    {kpi.isExpanded ? (
                      <ChevronUpIcon size={20} color={themeColors.iconColor} />
                    ) : (
                      <ChevronDownIcon size={20} color={themeColors.iconColor} />
                    )}
                  </TouchableOpacity>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    icon={<TrashIcon color={themeColors.iconColor} />}
                    accessibilityLabel={t('common.delete')}
                    onPress={() => removeKPI(kpi.id)}
                  />
                  {/* Title (flex: 1 fills remaining space) */}
                  <Text style={[styles.kpiHeaderTitle, { color: themeColors.kpiHeaderTitle }]}>{kpi.name || t('kpis.kpiName')}</Text>
                  {/* RIGHT side (in RTL): Number badge + Drag handle */}
                  <View style={[styles.kpiNumberBadge, { backgroundColor: themeColors.kpiNumberBadge }]}>
                    <Text style={[styles.kpiNumberText, { color: themeColors.kpiHeaderTitle }]}>{index + 1}</Text>
                  </View>
                  <MenuIcon size={20} color={themeColors.iconColor} />
                </View>

                {kpi.isExpanded && (
                  <View style={[styles.kpiContent, activeKpiDropdown?.kpiId === kpi.id && { zIndex: 100, overflow: 'visible' }]}>
                    {/* Basic Info Subsection */}
                    <View style={[styles.kpiSubsection, { backgroundColor: themeColors.kpiSubsection, borderColor: themeColors.sectionBorder }]}>
                      <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>
                      <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                        {/* KPI Name - RIGHT side in RTL */}
                        <View style={[styles.formGroup, styles.formGroupHalf]}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')} <Text style={styles.required}>*</Text></Text>
                          <TextInput
                            style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                            value={kpi.name}
                            onChangeText={(text) => updateKPI(kpi.id, 'name', text)}
                            placeholder=""
                            placeholderTextColor={themeColors.placeholder}
                            textAlign="right"
                          />
                        </View>
                        {/* Weight - LEFT side in RTL */}
                        <View style={[styles.formGroup, styles.formGroupHalf]}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')} <Text style={styles.required}>*</Text></Text>
                          <View style={styles.weightInputRow}>
                            <View style={[styles.weightInputWrapper, { backgroundColor: themeColors.weightInputWrapper, borderColor: themeColors.inputBorder }]}>
                              <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                              <TextInput
                                style={[styles.weightInput, { color: themeColors.inputText }]}
                                value={kpi.weight}
                                onChangeText={(text) => updateKPI(kpi.id, 'weight', normalizeArabicNumbers(text))}
                                placeholder="30"
                                placeholderTextColor={themeColors.placeholder}
                                keyboardType="numeric"
                                textAlign="right"
                              />
                            </View>
                            <TouchableOpacity style={[styles.weightButton, { backgroundColor: themeColors.weightButton, borderColor: themeColors.weightButtonBorder }]}>
                              <Text style={[styles.weightButtonText, { color: themeColors.label }]}>{t('kpis.setWeight')}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      </View>

                      <View style={styles.formGroup}>
                        <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiDescription')} <Text style={styles.required}>*</Text></Text>
                        <TextInput
                          style={[styles.input, styles.textarea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                          value={kpi.description}
                          onChangeText={(text) => updateKPI(kpi.id, 'description', text)}
                          placeholder=""
                          placeholderTextColor={themeColors.placeholder}
                          multiline
                          numberOfLines={2}
                          textAlign="right"
                          textAlignVertical="top"
                        />
                      </View>
                    </View>

                    {/* Classification Subsection */}
                    <View style={[styles.kpiSubsection, { backgroundColor: themeColors.kpiSubsection, borderColor: themeColors.sectionBorder, zIndex: 30, overflow: 'visible' }]}>
                      <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>{t('common.classification')}</Text>
                      <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 10, overflow: 'visible' }]}>
                        {/* KPI Type - RIGHT side in RTL */}
                        <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && { zIndex: 100 }]}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')} <Text style={styles.required}>*</Text></Text>
                          <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && { zIndex: 100 }]}>
                            <TouchableOpacity
                              style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                              onPress={() => toggleKpiDropdown(kpi.id, 'kpiType')}
                            >
                              <ChevronDownIcon size={16} color={themeColors.iconColor} />
                              <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, kpi.kpiType ? { color: themeColors.dropdownTextSelected } : undefined]}>
                                {kpiTypeOptions.find(o => o.value === kpi.kpiType)?.label || t('kpis.selectClassification')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && (
                              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder }]}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {kpiTypeOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                                      onPress={() => {
                                        updateKPI(kpi.id, 'kpiType', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>
                        {/* Polarity - LEFT side in RTL */}
                        <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && { zIndex: 100 }]}>
                          <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')} <Text style={styles.required}>*</Text></Text>
                          <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && { zIndex: 100 }]}>
                            <TouchableOpacity
                              style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                              onPress={() => toggleKpiDropdown(kpi.id, 'polarity')}
                            >
                              <ChevronDownIcon size={16} color={themeColors.iconColor} />
                              <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, kpi.polarity ? { color: themeColors.dropdownTextSelected } : undefined]}>
                                {polarityOptions.find(o => o.value === kpi.polarity)?.label || t('kpis.selectPolarity')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && (
                              <View style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder }]}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {polarityOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                                      onPress={() => {
                                        updateKPI(kpi.id, 'polarity', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>

                      <View style={{ marginTop: tokens.spacing.absolute['3'], zIndex: 5 }}>
                        <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                        <MultiSelectDropdown
                          values={kpi.responsibleUnits}
                          options={(() => {
                            const related = formData.organizationalUnits.map(String);
                            const opts = orgUnitOptions.filter(o => related.includes(o.value));
                            kpi.responsibleUnits.forEach(v => {
                              if (!opts.find(o => o.value === v)) {
                                const found = orgUnitOptions.find(o => o.value === v);
                                if (found) opts.push(found);
                              }
                            });
                            return opts;
                          })()}
                          placeholder={t('initiatives.selectResponsibleUnits')}
                          onChange={(vals) => updateKPI(kpi.id, 'responsibleUnits', vals)}
                        />
                      </View>

                    </View>

                    {/* Measurement Subsection */}
                    <View style={[styles.kpiSubsection, { backgroundColor: themeColors.kpiSubsection, borderColor: themeColors.sectionBorder, zIndex: 20, overflow: 'visible' }]}>
                      {/* Measurement + annual target grid — shared section for new AND existing KPIs */}
                      <KpiAnnualTargetSection
                        config={kpi.target}
                        onChange={(patch) => updateKPI(kpi.id, 'target', { ...kpi.target, ...patch })}
                        unit={kpi.unit}
                        frequency={kpi.frequency}
                        startDate={kpi.measurementStartDate}
                        formula={kpi.formula}
                        dataSource={kpi.dataSource}
                        onMeasurementChange={(field, value) =>
                          updateKPI(kpi.id, field === 'startDate' ? 'measurementStartDate' : field, value)
                        }
                      />
                    </View>
                  </View>
                )}
              </View>
            ))}

            {/* Add KPI Button */}
            <TouchableOpacity style={[styles.addKpiButton, { backgroundColor: themeColors.addKpiButton, borderColor: themeColors.addKpiButtonBorder }]} onPress={addKPI}>
              <PlusIcon size={16} color={themeColors.iconColor} />
              <Text style={[styles.addKpiButtonText, { color: themeColors.addKpiButtonText }]}>{t('kpis.addKPI')}</Text>
            </TouchableOpacity>
          </View>

          {/* Footer Buttons */}
          <View style={styles.footer}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={submitting}
            >
              {isEditMode ? t('common.update') : t('common.save')}
            </Button>
            <Button variant="secondary" onPress={requestCancel}>
              {t('common.cancel')}
            </Button>
          </View>
        </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  containerSmall: {
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

    mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },

  // Title
  pageTitle: {
    fontSize: tokens.typography["heading 3"]["font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },

  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    padding: tokens.spacing.absolute["6"],
    marginBottom: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["5"],
  },

  // Form
  formRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  formGroupHalf: {
    flex: 1,
    overflow: 'visible',
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  required: {
    color: COLORS.primary,
  },
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  textarea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },

  // Dropdown
  dropdownWrapper: {
    position: 'relative',
    zIndex: 10,
    overflow: 'visible',
  },
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    flex: 1,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  dropdownTextSelected: {
    color: COLORS.neutral900,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },

  // Weight Input
  weightInputRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  weightInputWrapper: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  percentSymbol: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginStart: tokens.spacing.absolute["2"],
  },
  weightInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
  },
  weightButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  weightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral700,
  },

  // Date Input
  dateInputWrapper: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dateInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
  },

  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacing.absolute["3"],
    backgroundColor: tokens.colors.raw.white,
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderTitle: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiNumberBadge: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  kpiNumberText: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    lineHeight: 26,
  },
  kpiContent: {
    padding: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  kpiSubsection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'visible',
  },
  kpiSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },

  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    direction: 'ltr' as any, // pin physical order: red Save always left, Cancel right (no RTL flip)
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
});

const styles = getStyles(true);
